import MembershipAlert from "../molecules/MembershipAlert";
import Dropdown from "../molecules/Dropdown";
import { formatDate } from "@/lib/utils";
import type { MembershipSummary } from "@/lib/api/types";

interface MembershipHeaderProps {
  /** ISO expiration date shown in the renewal alert. */
  expirationDate?: string;
  /** Whether Altru considers this membership within its renewal window. */
  isInRenewalWindow?: boolean;
  /** Current membership status — drives which alert variant is shown. */
  membershipStatus?: "ACTIVE" | "EXPIRED";
  memberships: MembershipSummary[];
  selectedMembershipId: string;
  onMembershipChange: (id: string) => void;
  /** Fallback label + id used when memberships array is empty (e.g. cards-only data). */
  fallbackOption?: { id: string; label: string };
}

/**
 * @component MembershipHeader
 * @description Header section of the membership sidebar. Displays a renewal
 * alert when expiring soon, and a dropdown when the user has multiple memberships.
 * @param expirationDate - ISO expiration date used for the expiring-soon check.
 * @param memberships - All memberships linked to the user's account.
 * @param selectedMembershipId - Currently selected membership ID.
 * @param onMembershipChange - Callback fired when the user selects a different membership.
 * @returns The membership sidebar header section.
 */
export default function MembershipHeader({
  expirationDate,
  isInRenewalWindow,
  membershipStatus,
  memberships,
  selectedMembershipId,
  onMembershipChange,
  fallbackOption,
}: MembershipHeaderProps) {
  const options = memberships.length > 0
    ? memberships.map((m) => ({ id: m.membershipId, label: m.membershipName }))
    : fallbackOption
      ? [fallbackOption]
      : [];
  return (
    <div className="flex flex-col gap-4">
      {/* Expired alert */}
      {membershipStatus === "EXPIRED" && (
        <MembershipAlert variant="expired" />
      )}

      {/* Renew alert — shown when Altru signals membership is in renewal window */}
      {membershipStatus !== "EXPIRED" && isInRenewalWindow && (
        <MembershipAlert
          variant="renew"
          expirationDate={expirationDate ? formatDate(expirationDate) : undefined}
        />
      )}

      {/* Membership dropdown */}
      <Dropdown
        options={options}
        selectedId={selectedMembershipId}
        onChange={onMembershipChange}
      />
    </div>
  );
}
